import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between w-full" }
const _hoisted_2 = { class: "text-xl font-bold text-textPrimary" }
const _hoisted_3 = { class: "flex items-center justify-between w-full mt-2" }

import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { ImageState, DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import Network from '@/Network'
import type { DsAttendanceUploadForm } from './types'
import type {
  DSAttendanceUploadExtras,
  ScheduleShiftsDto
} from '@/Network/Types/Requests/DsAttendanceRequest'
import type { DSAttendanceUploadResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import StepForm from './StepForm.vue'
import StepFile from './StepFile.vue'
import StepPreview from './StepPreview.vue'
import StepChecklist from './StepChecklist.vue'
import StepWarnings from './StepWarnings.vue'
import { buildSelfCheckPayload, emptySelfCheckState } from './self-check'

interface Props {
  modelValue: boolean
  /**
   * Campos que chegam da vaga de origem (M2, §8.2). O modal os usa como semente
   * do formulário a cada abertura; ausente, o wizard nasce vazio como sempre.
   */
  initialForm?: Partial<DsAttendanceUploadForm> | null
  /** Quais desses campos ficam bloqueados no passo 1. Vazio = tudo editável. */
  lockedFields?: string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  props: {
    modelValue: { type: Boolean },
    initialForm: { default: null },
    lockedFields: { default: () => [] }
  },
  emits: ['update:modelValue', 'save'],
  setup(__props: any, { emit }) {

const props = __props;




/**
 * Passos nomeados: o fluxo passou de 3 para 5 com a qualidade na origem (Eixo 2 da discovery
 * 2026-09-11), e `currentStep === 3` espalhado pelo template já não dizia qual passo era.
 * CHECKLIST só existe nas modalidades de folha; WARNINGS só quando o backend devolve avisos.
 */
const STEP = {
  FORM: 1,
  FILE: 2,
  PREVIEW: 3,
  CHECKLIST: 4,
  WARNINGS: 5
} as const

const currentStep = ref<number>(STEP.FORM)
const isSaving = ref(false)

const selectedFile = ref<File | null>(null)
const rotation = ref(0)
const zoomScale = ref(1)

/** Páginas anexadas — 1 para imagem, total do PDF quando o renderizador as conta. */
const attachedPages = ref(1)
const selfCheckState = ref(emptySelfCheckState())
const qualityWarnings = ref<string[]>([])

const shiftStore = useShiftStore()
const { listShifts } = storeToRefs(shiftStore)

const getDefaultForm = (): DsAttendanceUploadForm => ({
  uploadedBy: null,
  company: null,
  healthUnit: null,
  schedules: [],
  shifts: [],
  date: null,
  imageState: ImageState.PENDING,
  uploadAnother: false,
  monitoringType: '',
  monitoringTypeLabel: ''
})

/**
 * Semente do formulário: o default de sempre, sobrescrito pelo que a vaga já
 * sabe. Cópia por spread — `initialForm` é do chamador e não pode ser mutado
 * pelos campos que o emissor ainda vai preencher.
 *
 * `company`/`healthUnit` chegam como número (id da API) de uns chamadores e como
 * string (value das stores) de outros. É de propósito: a tolerância vive no
 * `value-comparator` dos selects do `StepForm`, não numa coerção aqui.
 */
const buildInitialForm = (): DsAttendanceUploadForm => ({
  ...getDefaultForm(),
  ...(props.initialForm ?? {})
})

const form = ref<DsAttendanceUploadForm>(buildInitialForm())

const handleClose = () => {
  emit('update:modelValue', false)
  setTimeout(resetModal, 300)
}

const resetModal = () => {
  currentStep.value = STEP.FORM
  selectedFile.value = null
  rotation.value = 0
  zoomScale.value = 1
  attachedPages.value = 1
  selfCheckState.value = emptySelfCheckState()
  qualityWarnings.value = []
  form.value = buildInitialForm()
}

/**
 * Modalidades de folha física: são as que têm páginas para conferir e as que produzem
 * `INCOMPLETE_SHEET`. App externo e app Minha Escala não passam pelo checklist.
 */
/**
 * Checklist do emissor só na FOLHA diária: `POST /upload` é o único endpoint que aceita
 * `selfCheck`/`expectedPages`/`qualityWarnings`. Numa folha não diária o passo coletaria uma
 * declaração que o backend descarta.
 *
 * Fase 3: estender `upload-non-daily` com os mesmos campos e devolver `NON_DAILY_SHEET` aqui.
 */
const needsSelfCheck = computed(
  () => form.value.monitoringType === DSAttendanceMonitoringType.FOLHA
)

const stepTitle = computed(() => {
  switch (currentStep.value) {
    case STEP.FORM:
      return 'Upload de Registro de Plantão'
    case STEP.FILE:
      return 'Selecione o Arquivo'
    case STEP.PREVIEW:
      return 'Confirme o Arquivo'
    case STEP.CHECKLIST:
      return 'Antes de enviar, confira a folha'
    default:
      return 'Avisos da folha enviada'
  }
})

const mainButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Enviar assim mesmo'
  if (currentStep.value === STEP.WARNINGS) return 'Concluir'
  return currentStep.value < STEP.PREVIEW ? 'Próximo' : 'Salvar'
})

const backButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Voltar e corrigir'
  if (currentStep.value === STEP.WARNINGS) return 'Refazer a foto'
  return currentStep.value === STEP.FORM ? 'Cancelar' : 'Voltar'
})

const handleBackAction = () => {
  // "Refazer a foto": a folha já subiu, então o caminho é um envio novo do mesmo registro —
  // volta ao passo do arquivo mantendo o formulário (unidade, escalas, data) preenchido.
  if (currentStep.value === STEP.WARNINGS) {
    qualityWarnings.value = []
    selectedFile.value = null
    currentStep.value = STEP.FILE
    return
  }

  if (currentStep.value > STEP.FORM) {
    currentStep.value--
  } else {
    handleClose()
  }
}

const isStepFormValid = ref(false)

const toast = useToast()
const { notifyError } = useErrorToast()

const formatDateToISO = (date: Date | string | null): string => {
  if (!date) return ''
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

/** Data do registro em dd/MM/yyyy — é como o emissor a lê na folha física. */
const formattedReferenceDate = computed(() => {
  const iso = formatDateToISO(form.value.date)
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${year}`
})

const healthUnitIdOrNull = computed(() => {
  const id = Number(form.value.healthUnit)
  return Number.isFinite(id) && id > 0 ? id : null
})

const selectedScheduleIdsAsNumbers = computed(() =>
  form.value.schedules.map(Number).filter((id) => Number.isFinite(id) && id > 0)
)

const buildShiftsBySchedule = (): ScheduleShiftsDto[] => {
  const selectedScheduleIds = new Set(form.value.schedules.map(Number))
  const selectedShiftIds = new Set(form.value.shifts.map(Number))

  if (!selectedShiftIds.size) {
    return Array.from(selectedScheduleIds, (scheduleId) => ({
      scheduleId,
      shiftIds: []
    }))
  }

  const grouped = new Map<number, number[]>()

  for (const shift of listShifts.value) {
    const shiftId = Number(shift.id)
    const sid = Number(shift.schedule.id)

    if (!selectedShiftIds.has(shiftId) || !selectedScheduleIds.has(sid)) continue

    const shiftIds = grouped.get(sid) ?? []
    shiftIds.push(shiftId)
    grouped.set(sid, shiftIds)
  }

  // Assume que todas as escalas selecionadas têm turnos carregados no listShifts.
  // Isso é garantido pelo fluxo normal (fetchShifts carrega turnos das escalas selecionadas),
  // mas verificação defensiva avisa se uma escala selecionada está faltando no store (inconsistência de dados).
  const foundScheduleIds = new Set(grouped.keys())
  const missingSchedules = Array.from(selectedScheduleIds).filter((id) => !foundScheduleIds.has(id))
  if (missingSchedules.length > 0) {
    console.debug(
      `[AttendanceUpload] Escalas selecionadas sem turnos no store: ${missingSchedules.join(', ')}. Isso pode indicar uma inconsistência de dados.`
    )
  }

  return Array.from(grouped, ([scheduleId, shiftIds]) => ({ scheduleId, shiftIds }))
}

/**
 * Extras do Eixo 2A. Só vão quando o passo do checklist existiu: numa modalidade sem folha,
 * um `selfCheck` vazio seria uma declaração que ninguém fez.
 */
const buildUploadExtras = (): DSAttendanceUploadExtras | undefined => {
  if (!needsSelfCheck.value) return undefined
  return {
    selfCheck: buildSelfCheckPayload(selfCheckState.value),
    expectedPages: attachedPages.value
  }
}

const uploadEvidence = async (): Promise<DSAttendanceUploadResponse> => {
  const referenceDate = formatDateToISO(form.value.date)
  const healthUnitId = Number(form.value.healthUnit)
  const schedules = buildShiftsBySchedule()

  if (form.value.monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP) {
    return await Network.dsAttendance.uploadExternalEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId
    )
  }

  const uploadedById = Number(form.value.uploadedBy)
  const imageState = form.value.imageState ?? ImageState.PENDING
  const extras = buildUploadExtras()

  if (form.value.monitoringType === DSAttendanceMonitoringType.NON_DAILY_SHEET) {
    return await Network.dsAttendance.uploadNonDailyEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId,
      uploadedById,
      rotation.value,
      imageState,
      extras
    )
  }

  return await Network.dsAttendance.uploadEvidence(
    selectedFile.value!,
    schedules,
    referenceDate,
    healthUnitId,
    uploadedById,
    rotation.value,
    imageState,
    extras
  )
}

const finishUpload = () => {
  emit('save')
  form.value.uploadAnother ? resetModal() : handleClose()
}

const handleMainAction = async () => {
  if (currentStep.value < STEP.PREVIEW) {
    currentStep.value++
    return
  }

  // "Concluir" no painel de avisos: a folha já subiu no passo anterior.
  if (currentStep.value === STEP.WARNINGS) {
    finishUpload()
    return
  }

  if (!selectedFile.value) {
    handleClose()
    return
  }

  // Folha física ganha o passo do checklist entre a pré-visualização e o POST.
  if (currentStep.value === STEP.PREVIEW && needsSelfCheck.value) {
    currentStep.value = STEP.CHECKLIST
    return
  }

  try {
    isSaving.value = true
    const response = await uploadEvidence()
    toast.success('Upload realizado com sucesso!')
    // Eixo 2B — avisos de qualidade não bloqueiam nem desfazem: o registro está enviado, e o
    // painel só oferece reenviar uma foto melhor. Sem avisos, o modal fecha como sempre.
    const warnings = response?.qualityWarnings ?? []
    // O `save` sai uma única vez, no `finishUpload` do "Concluir" — emitir aqui recarregava a
    // lista por baixo do painel de avisos que o emissor ainda está lendo.
    if (warnings.length > 0) {
      qualityWarnings.value = warnings
      currentStep.value = STEP.WARNINGS
      return
    }
    finishUpload()
  } catch (error: unknown) {
    // `error instanceof Error ? message : fallback` era um funil caseiro: repassava o
    // texto técnico de um TypeError e ignorava o tratamento de rede/timeout/5xx.
    console.error('Erro ao salvar registro:', error)
    notifyError(error, 'Não foi possível enviar o registro de presença.')
  } finally {
    isSaving.value = false
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) resetModal()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalVue, {
    "modal-active": _ctx.modelValue,
    "has-header-border": "",
    "has-footer-border": "",
    onClose: handleClose,
    "max-width": currentStep.value === STEP.PREVIEW ? 'max-w-[1000px]' : 'max-w-[500px]',
    class: "register-upload-modal"
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", _hoisted_2, _toDisplayString(stepTitle.value), 1)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(OutlinedButton, {
          "data-testid": "upload-modal-voltar-button",
          text: backButtonText.value,
          width: "160px",
          onClick: handleBackAction,
          class: "!border-primary30 !text-primary30 !font-bold"
        }, null, 8, ["text"]),
        _createVNode(FilledButton, {
          "data-testid": "upload-modal-confirmar-button",
          text: mainButtonText.value,
          width: "180px",
          onClick: handleMainAction,
          loading: isSaving.value,
          disabled: 
            (currentStep.value === STEP.FORM && !isStepFormValid.value) ||
            (currentStep.value === STEP.FILE && !selectedFile.value)
          ,
          class: "!bg-primary !text-white !font-bold hover:!bg-primary40"
        }, null, 8, ["text", "loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      (currentStep.value === STEP.FORM)
        ? (_openBlock(), _createBlock(StepForm, {
            key: 0,
            "model-value": form.value,
            "locked-fields": _ctx.lockedFields,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = (val) => Object.assign(form.value, val)),
            valid: isStepFormValid.value,
            "onUpdate:valid": _cache[1] || (_cache[1] = ($event: any) => ((isStepFormValid).value = $event))
          }, null, 8, ["model-value", "locked-fields", "valid"]))
        : (currentStep.value === STEP.FILE)
          ? (_openBlock(), _createBlock(StepFile, {
              key: 1,
              modelValue: selectedFile.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedFile).value = $event))
            }, null, 8, ["modelValue"]))
          : (currentStep.value === STEP.PREVIEW)
            ? (_openBlock(), _createBlock(StepPreview, {
                key: 2,
                file: selectedFile.value,
                rotation: rotation.value,
                "onUpdate:rotation": _cache[3] || (_cache[3] = ($event: any) => ((rotation).value = $event)),
                zoom: zoomScale.value,
                "onUpdate:zoom": _cache[4] || (_cache[4] = ($event: any) => ((zoomScale).value = $event)),
                "monitoring-type-label": form.value.monitoringTypeLabel ?? '',
                "onUpdate:totalPages": _cache[5] || (_cache[5] = ($event: any) => (attachedPages.value = $event))
              }, null, 8, ["file", "rotation", "zoom", "monitoring-type-label"]))
            : (currentStep.value === STEP.CHECKLIST)
              ? (_openBlock(), _createBlock(StepChecklist, {
                  key: 3,
                  modelValue: selfCheckState.value,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selfCheckState).value = $event)),
                  "formatted-date": formattedReferenceDate.value,
                  "health-unit-id": healthUnitIdOrNull.value,
                  "schedule-ids": selectedScheduleIdsAsNumbers.value,
                  "reference-date": formatDateToISO(form.value.date),
                  pages: attachedPages.value
                }, null, 8, ["modelValue", "formatted-date", "health-unit-id", "schedule-ids", "reference-date", "pages"]))
              : (currentStep.value === STEP.WARNINGS)
                ? (_openBlock(), _createBlock(StepWarnings, {
                    key: 4,
                    warnings: qualityWarnings.value
                  }, null, 8, ["warnings"]))
                : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modal-active", "max-width"]))
}
}

})